import { Suspense, lazy, type ReactNode } from "react";
import { createBrowserRouter, Navigate, Outlet, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AppLayout } from "../components/layout/AppLayout";
import { LoadingState } from "../components/common/LoadingState";
import { EmptyState } from "../components/common/EmptyState";
import { SeoManager } from "../components/common/SeoManager";
import { apiClient } from "../lib/apiClient";
import { isAdminRole, useAuthStore } from "../features/auth/authStore";
import { getRecoveryAccessTokenFromUrl } from "../features/auth/authService";

const AdminPage = lazy(() => import("../features/admin/AdminPage").then((module) => ({ default: module.AdminPage })));
const LoginPage = lazy(() => import("../features/auth/LoginPage").then((module) => ({ default: module.LoginPage })));
const AdminLoginPage = lazy(() => import("../features/auth/LoginPage").then((module) => ({ default: module.AdminLoginPage })));
const ResetPasswordPage = lazy(() => import("../features/auth/ResetPasswordPage").then((module) => ({ default: module.ResetPasswordPage })));
const PublicCloudflareGuidePage = lazy(() =>
  import("../features/cloudflare/CloudflareGuidePage").then((module) => ({ default: module.PublicCloudflareGuidePage }))
);
const PublicVercelGuidePage = lazy(() =>
  import("../features/vercel/VercelGuidePage").then((module) => ({ default: module.PublicVercelGuidePage }))
);
const DiagnosisPage = lazy(() => import("../features/diagnosis/DiagnosisPage").then((module) => ({ default: module.DiagnosisPage })));
const DiagnosisResultPage = lazy(() =>
  import("../features/diagnosis/DiagnosisResultPage").then((module) => ({ default: module.DiagnosisResultPage }))
);
const DiagnosisRunningPage = lazy(() =>
  import("../features/diagnosis/DiagnosisRunningPage").then((module) => ({ default: module.DiagnosisRunningPage }))
);
const DesignPage = lazy(() => import("../features/design/DesignPage").then((module) => ({ default: module.DesignPage })));
const DesignResultPage = lazy(() => import("../features/design/DesignResultPage").then((module) => ({ default: module.DesignResultPage })));
const DesignRunningPage = lazy(() => import("../features/design/DesignRunningPage").then((module) => ({ default: module.DesignRunningPage })));
const DeepDivePage = lazy(() => import("../features/deep-dive/DeepDivePage").then((module) => ({ default: module.DeepDivePage })));
const DeepDiveDetailPage = lazy(() =>
  import("../features/deep-dive/DeepDiveDetailPage").then((module) => ({ default: module.DeepDiveDetailPage }))
);
const PublicGlossaryPage = lazy(() =>
  import("../features/glossary/GlossaryPage").then((module) => ({ default: module.PublicGlossaryPage }))
);
const HomePage = lazy(() => import("../features/home/HomePage").then((module) => ({ default: module.HomePage })));
const IdeaDetailPage = lazy(() => import("../features/ideas/IdeaDetailPage").then((module) => ({ default: module.IdeaDetailPage })));
const CompanyPage = lazy(() => import("../features/legal/LegalPages").then((module) => ({ default: module.CompanyPage })));
const PrivacyPolicyPage = lazy(() => import("../features/legal/LegalPages").then((module) => ({ default: module.PrivacyPolicyPage })));
const VibeMarketingPage = lazy(() =>
  import("../features/marketing/VibeMarketingPage").then((module) => ({ default: module.VibeMarketingPage }))
);
const VibeDeployPage = lazy(() =>
  import("../features/vibe-deploy/VibeDeployPage").then((module) => ({ default: module.VibeDeployPage }))
);
const PreLaunchPage = lazy(() =>
  import("../features/pre-launch/PreLaunchPage").then((module) => ({ default: module.PreLaunchPage }))
);
const OnboardingPage = lazy(() => import("../features/onboarding/OnboardingPage").then((module) => ({ default: module.OnboardingPage })));
const ProfileSetupPage = lazy(() => import("../features/profile/ProfileSetupPage").then((module) => ({ default: module.ProfileSetupPage })));
const QuestionFlowPage = lazy(() =>
  import("../features/questions/QuestionFlowPage").then((module) => ({ default: module.QuestionFlowPage }))
);
const ResultPage = lazy(() => import("../features/questions/ResultPage").then((module) => ({ default: module.ResultPage })));
const SavedIdeasPage = lazy(() => import("../features/saved/SavedIdeasPage").then((module) => ({ default: module.SavedIdeasPage })));
const SettingsPage = lazy(() => import("../features/settings/SettingsPage").then((module) => ({ default: module.SettingsPage })));
const SwipePage = lazy(() => import("../features/swipe/SwipePage").then((module) => ({ default: module.SwipePage })));
const SwipeResultPage = lazy(() => import("../features/swipe/SwipeResultPage").then((module) => ({ default: module.SwipeResultPage })));

function lazyRoute(element: ReactNode) {
  return <Suspense fallback={<LoadingState label="画面を読み込んでいます..." />}>{element}</Suspense>;
}

function DocumentEffects() {
  return (
    <>
      <SeoManager />
      <Outlet />
    </>
  );
}

function EntryRedirect() {
  const role = useAuthStore((state) => state.role);
  const status = useAuthStore((state) => state.status);
  const profileQuery = useQuery({ queryKey: ["profile"], queryFn: apiClient.getProfile, enabled: status === "authenticated" && Boolean(role) });

  if (getRecoveryAccessTokenFromUrl()) return <Navigate to={`/reset-password${window.location.hash}`} replace />;

  if (status === "loading") return <LoadingState label="ログイン状態を確認しています..." fullScreen />;
  if (status === "unauthenticated" || !role) return <Navigate to="/login" replace />;

  if (profileQuery.isLoading) {
    return <LoadingState label="VibeGenieを準備しています..." fullScreen />;
  }

  if (profileQuery.isError) {
    return (
      <EmptyState
        title="APIに接続できません"
        description="D1 migrationとWorker起動状態を確認してください。ローカルでAPI込み確認をする場合は npm run preview を使います。"
        actionLabel="再読み込み"
        onAction={() => window.location.reload()}
      />
    );
  }

  if (!profileQuery.data?.profileSetupCompleted) return <Navigate to="/profile/setup" replace />;
  if (isAdminRole(role)) return <Navigate to="/admin/cost" replace />;

  return <Navigate to={profileQuery.data?.onboardingCompleted ? "/home" : "/onboarding"} replace />;
}

function RequireOnboardingComplete() {
  const location = useLocation();
  const role = useAuthStore((state) => state.role);
  const status = useAuthStore((state) => state.status);
  const isAdminPath = location.pathname.startsWith("/admin");
  const profileQuery = useQuery({ queryKey: ["profile"], queryFn: apiClient.getProfile, enabled: status === "authenticated" && Boolean(role) });

  if (status === "loading") return <LoadingState label="ログイン状態を確認しています..." fullScreen />;
  if (status === "unauthenticated" || !role) return <Navigate to={isAdminPath ? "/admin/login" : "/login"} replace />;

  if (profileQuery.isLoading) {
    return <LoadingState label="プロフィールを読み込んでいます..." fullScreen />;
  }

  if (profileQuery.isError) {
    return (
      <EmptyState
        title="プロフィールを読み込めません"
        description="ネットワークまたはD1の設定を確認してから、もう一度お試しください。"
        actionLabel="再読み込み"
        onAction={() => window.location.reload()}
      />
    );
  }

  if (!profileQuery.data?.profileSetupCompleted) {
    return <Navigate to="/profile/setup" replace />;
  }

  if (isAdminPath) {
    return isAdminRole(role) ? <Outlet /> : <Navigate to="/home" replace />;
  }

  if (!profileQuery.data?.onboardingCompleted && location.pathname !== "/settings") {
    return <Navigate to="/onboarding" replace />;
  }

  return <Outlet />;
}

function RequireProfileSetupComplete() {
  const role = useAuthStore((state) => state.role);
  const status = useAuthStore((state) => state.status);
  const profileQuery = useQuery({ queryKey: ["profile"], queryFn: apiClient.getProfile, enabled: status === "authenticated" && Boolean(role) });

  if (status === "loading") return <LoadingState label="ログイン状態を確認しています..." fullScreen />;
  if (status === "unauthenticated" || !role) return <Navigate to="/login" replace />;

  if (profileQuery.isLoading) return <LoadingState label="プロフィールを読み込んでいます..." fullScreen />;
  if (profileQuery.isError) {
    return (
      <EmptyState
        title="プロフィールを読み込めません"
        description="ネットワークまたはD1の設定を確認してから、もう一度お試しください。"
        actionLabel="再読み込み"
        onAction={() => window.location.reload()}
      />
    );
  }

  if (!profileQuery.data?.profileSetupCompleted) return <Navigate to="/profile/setup" replace />;
  if (isAdminRole(role)) return <Navigate to="/admin/cost" replace />;

  return <Outlet />;
}

export const router = createBrowserRouter([
  {
    element: <DocumentEffects />,
    children: [
      { path: "/", element: <EntryRedirect /> },
      { path: "/login", element: lazyRoute(<LoginPage />) },
      { path: "/admin/login", element: lazyRoute(<AdminLoginPage />) },
      { path: "/reset-password", element: lazyRoute(<ResetPasswordPage />) },
      { path: "/cloudflare", element: lazyRoute(<PublicCloudflareGuidePage />) },
      { path: "/vercel", element: lazyRoute(<PublicVercelGuidePage />) },
      { path: "/glossary", element: lazyRoute(<PublicGlossaryPage />) },
      { path: "/privacy-policy", element: lazyRoute(<PrivacyPolicyPage />) },
      { path: "/company", element: lazyRoute(<CompanyPage />) },
      { path: "/profile/setup", element: lazyRoute(<ProfileSetupPage />) },
      {
        element: <RequireProfileSetupComplete />,
        children: [{ path: "/onboarding", element: lazyRoute(<OnboardingPage />) }]
      },
      {
        element: <RequireOnboardingComplete />,
        children: [
          {
            element: <AppLayout />,
            children: [
              { path: "/home", element: lazyRoute(<HomePage />) },
              { path: "/questions", element: lazyRoute(<QuestionFlowPage />) },
              { path: "/deep-dive", element: lazyRoute(<DeepDivePage />) },
              { path: "/deep-dive/:id/detail", element: lazyRoute(<DeepDiveDetailPage />) },
              { path: "/diagnosis", element: lazyRoute(<DiagnosisPage />) },
              { path: "/diagnosis/:id/running", element: lazyRoute(<DiagnosisRunningPage />) },
              { path: "/diagnosis/:id/result", element: lazyRoute(<DiagnosisResultPage />) },
              { path: "/design", element: lazyRoute(<DesignPage />) },
              { path: "/design/:id/running", element: lazyRoute(<DesignRunningPage />) },
              { path: "/design/:id/result", element: lazyRoute(<DesignResultPage />) },
              { path: "/vibe-deploy", element: lazyRoute(<VibeDeployPage />) },
              { path: "/pre-launch", element: lazyRoute(<PreLaunchPage />) },
              { path: "/marketing", element: lazyRoute(<VibeMarketingPage />) },
              { path: "/result", element: lazyRoute(<ResultPage />) },
              { path: "/swipe", element: lazyRoute(<SwipePage />) },
              { path: "/swipe/result", element: lazyRoute(<SwipeResultPage />) },
              { path: "/saved", element: lazyRoute(<SavedIdeasPage />) },
              { path: "/ideas/:id", element: lazyRoute(<IdeaDetailPage />) },
              { path: "/settings", element: lazyRoute(<SettingsPage />) },
              { path: "/admin", element: <Navigate to="/admin/cost" replace /> },
              { path: "/admin/:section", element: lazyRoute(<AdminPage />) }
            ]
          }
        ]
      },
      { path: "*", element: <Navigate to="/" replace /> }
    ]
  }
]);
